import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { playSound } from '../../shared/soundEngine';
import { getClients, getCoachProfile, updateCoachProfile } from './coachApi';
import CoachClientsTab from './CoachClientsTab';
import CoachSessionsTab from './CoachSessionsTab';
import CoachNotesTab from './CoachNotesTab';
import CoachToolkitTab from './CoachToolkitTab';
import CoachTeamsTab from './CoachTeamsTab';
import CoachScheduleTab from './CoachScheduleTab';
import CoachTournamentsTab from './CoachTournamentsTab';
import CoachGymTab from './CoachGymTab';
import CoachAnalyticsTab from './CoachAnalyticsTab';
import CoachReadinessTab from './CoachReadinessTab';
import CoachWalletTab from './CoachWalletTab';

// Tier extracted from license string: 'coaches_solo' → 'solo'
const getTier = (license) => {
  if (!license) return 'solo';
  const parts = license.split('_');
  return parts[1] || 'solo';
};

// Feature availability per tier
const TIER_TABS = {
  solo:  ['clients', 'sessions', 'notes', 'toolkit', 'readiness', 'analytics', 'wallet'],
  club:  ['clients', 'sessions', 'notes', 'toolkit', 'readiness', 'analytics', 'teams', 'schedule', 'wallet'],
  pro:   ['clients', 'sessions', 'notes', 'toolkit', 'readiness', 'analytics', 'teams', 'schedule', 'tournaments', 'gym', 'wallet'],
  elite: ['clients', 'sessions', 'notes', 'toolkit', 'readiness', 'analytics', 'teams', 'schedule', 'tournaments', 'gym', 'wallet'],
};

const ALL_TABS = [
  { id: 'clients',     icon: '👥', label: 'Clients',     labelNl: 'Klanten' },
  { id: 'sessions',    icon: '📅', label: 'Sessions',    labelNl: 'Sessies' },
  { id: 'notes',       icon: '📝', label: 'Notes',       labelNl: 'Notities' },
  { id: 'toolkit',     icon: '🛠', label: 'Toolkit',     labelNl: 'Toolkit' },
  { id: 'readiness',   icon: '🎯', label: 'Readiness',   labelNl: 'Gereedheid' },
  { id: 'analytics',   icon: '📊', label: 'Analytics',   labelNl: 'Analytiek' },
  { id: 'teams',       icon: '🏆', label: 'Teams',       labelNl: 'Teams' },
  { id: 'schedule',    icon: '🗓', label: 'Schedule',    labelNl: 'Planning' },
  { id: 'tournaments', icon: '🥇', label: 'Tournaments', labelNl: 'Toernooien' },
  { id: 'gym',         icon: '🏋️', label: 'Gym',         labelNl: 'Gym' },
  { id: 'wallet',      icon: '💰', label: 'Wallet',      labelNl: 'Wallet' },
];

const TIER_COLORS = { solo: '#5B9ED0', club: '#4AC87A', pro: '#C8A84B', elite: '#7B6FD0' };
const TIER_LABELS = { solo: 'Solo', club: 'Club', pro: 'Pro', elite: 'Elite' };

export default function CoachDashboardScreen({ userId, license, lang, businessId }) {
  const nl = lang === 'nl';
  const tier = getTier(license);
  const coachId = userId;
  const allowedTabs = TIER_TABS[tier] || TIER_TABS.solo;

  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients]     = useState([]);
  const [profile, setProfile]     = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', bio: '', specialty: '', website: '', rate: '', is_listed: false });
  const [savingProfile, setSavingProfile] = useState(false);

  const loadClients = useCallback(() => {
    getClients(coachId).then(d => { if (Array.isArray(d)) setClients(d); }).catch(() => {});
  }, [coachId]);

  const loadProfile = useCallback(() => {
    getCoachProfile(coachId).then(d => {
      if (d) {
        setProfile(d);
        setProfileForm({ name: d.name || '', bio: d.bio || '', specialty: d.specialty || '', website: d.website || '', rate: d.rate != null ? String(d.rate) : '', is_listed: !!d.is_listed });
      }
    }).catch(() => {});
  }, [coachId]);

  useEffect(() => { loadClients(); loadProfile(); }, [loadClients, loadProfile]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    await updateCoachProfile(coachId, profileForm).catch(() => {});
    setSavingProfile(false);
    setShowProfile(false);
    loadProfile();
  };

  const tabsToShow = ALL_TABS.filter(t => allowedTabs.includes(t.id));
  const lockedTabs = ALL_TABS.filter(t => !allowedTabs.includes(t.id));

  const tierColor = TIER_COLORS[tier] || _T.accent;

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: _T.bg, paddingBottom: 80 }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ padding: '20px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ color: C.white, fontSize: 22, fontWeight: 800 }}>{nl ? 'Coach Dashboard' : 'Coach Dashboard'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <div style={{ background: `${tierColor}20`, border: `1px solid ${tierColor}55`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: tierColor }}>
                {TIER_LABELS[tier]}
              </div>
              <div style={{ color: C.silver2, fontSize: 11 }}>{clients.length} {nl ? 'klanten' : 'clients'}</div>
            </div>
          </div>
          <button onClick={() => setShowProfile(v => !v)}
            style={{ width: 44, height: 44, borderRadius: '50%', background: `${_T.accent}20`, border: `2px solid ${_T.accent}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, cursor: 'pointer' }}>
            {profile?.avatar || '👤'}
          </button>
        </div>

        {/* Quick stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          <div style={{ background: _T.bg3, borderRadius: 12, padding: 12, border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
            <div style={{ color: C.white, fontSize: 20, fontWeight: 800 }}>{clients.length}</div>
            <div style={{ color: C.silver2, fontSize: 9, marginTop: 2 }}>{nl ? 'KLANTEN' : 'CLIENTS'}</div>
          </div>
          <div style={{ background: _T.bg3, borderRadius: 12, padding: 12, border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
            <div style={{ color: C.white, fontSize: 20, fontWeight: 800 }}>
              {clients.filter(c => c.avg_mood >= 4).length}
            </div>
            <div style={{ color: C.silver2, fontSize: 9, marginTop: 2 }}>{nl ? 'GOED GEVOEL' : 'FEELING GOOD'}</div>
          </div>
          <div style={{ background: _T.bg3, borderRadius: 12, padding: 12, border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
            <div style={{ color: clients.filter(c => c.avg_sleep && c.avg_sleep < 6).length > 0 ? '#C05050' : '#4AC87A', fontSize: 20, fontWeight: 800 }}>
              {clients.filter(c => c.avg_sleep && c.avg_sleep < 6).length}
            </div>
            <div style={{ color: C.silver2, fontSize: 9, marginTop: 2 }}>{nl ? 'SLAAPPROBLEEM' : 'SLEEP ISSUE'}</div>
          </div>
        </div>
      </div>

      {/* Profile edit panel */}
      {showProfile && (
        <div style={{ margin: '0 16px 16px', background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: 16 }}>
          <div style={{ fontSize: 10, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'COACH PROFIEL' : 'COACH PROFILE'}</div>
          {[
            { key: 'name', placeholder: nl ? 'Naam' : 'Name' },
            { key: 'bio', placeholder: nl ? 'Bio / Over mij' : 'Bio / About me' },
            { key: 'specialty', placeholder: nl ? 'Specialiteit (bijv. mentale coaching)' : 'Specialty (e.g. mental coaching)' },
            { key: 'website', placeholder: nl ? 'Website (optioneel)' : 'Website (optional)' },
            { key: 'rate', placeholder: nl ? 'Tarief per sessie in € (optioneel)' : 'Rate per session in € (optional)', type: 'number' },
          ].map(({ key, placeholder, type }) => (
            <input key={key} value={profileForm[key]} onChange={e => setProfileForm(f => ({ ...f, [key]: e.target.value }))}
              placeholder={placeholder} type={type || 'text'}
              style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 8, outline: 'none', boxSizing: 'border-box' }} />
          ))}
          {/* Marketplace listing toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 12px', background: _T.bg4, borderRadius: 10, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: C.white, fontSize: 12, fontWeight: 600 }}>{nl ? 'Zichtbaar in marketplace' : 'Listed in marketplace'}</div>
              <div style={{ color: C.silver, fontSize: 10 }}>{nl ? 'Laat klanten je profiel vinden' : 'Let clients find your profile'}</div>
            </div>
            <button
              onClick={() => setProfileForm(f => ({ ...f, is_listed: !f.is_listed }))}
              style={{
                width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: profileForm.is_listed ? _T.accent : 'rgba(255,255,255,0.15)',
                transition: 'background 0.2s', flexShrink: 0, position: 'relative',
              }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: '50%', background: '#fff',
                position: 'absolute', top: 3,
                left: profileForm.is_listed ? 23 : 3,
                transition: 'left 0.2s',
              }} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowProfile(false)} style={{ flex: 1, background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
              {nl ? 'Annuleren' : 'Cancel'}
            </button>
            <button onClick={handleSaveProfile} disabled={savingProfile}
              style={{ flex: 1, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontWeight: 700, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
              {savingProfile ? '...' : (nl ? 'Opslaan' : 'Save')}
            </button>
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div style={{ overflowX: 'auto', paddingBottom: 4, margin: '0 0 4px' }}>
        <div style={{ display: 'flex', gap: 0, padding: '0 16px', minWidth: 'max-content' }}>
          {tabsToShow.map(tab => {
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => { playSound('tab'); setActiveTab(tab.id); }}
                style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '10px 12px', border: 'none', background: 'none', borderBottom: `2.5px solid ${active ? _T.accent : 'transparent'}`, cursor: 'pointer', transition: 'all 0.2s' }}>
                <span style={{ fontSize: 18 }}>{tab.icon}</span>
                <span style={{ fontSize: 9, fontWeight: active ? 700 : 400, color: active ? _T.accent : C.silver2, whiteSpace: 'nowrap' }}>
                  {nl ? tab.labelNl : tab.label}
                </span>
              </button>
            );
          })}
          {/* Locked tab hints */}
          {lockedTabs.length > 0 && (
            <button onClick={() => {}}
              style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '10px 12px', border: 'none', background: 'none', borderBottom: '2.5px solid transparent', cursor: 'pointer', opacity: 0.4 }}>
              <span style={{ fontSize: 18 }}>🔒</span>
              <span style={{ fontSize: 9, color: C.silver2, whiteSpace: 'nowrap' }}>+{lockedTabs.length}</span>
            </button>
          )}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: _T.cardBorderBot, marginBottom: 16 }} />

      {/* Tab content */}
      <div style={{ padding: '0 16px' }}>
        {activeTab === 'clients' && (
          <CoachClientsTab coachId={coachId} tier={tier} clients={clients} lang={lang} onClientsChange={setClients} />
        )}
        {activeTab === 'sessions' && (
          <CoachSessionsTab coachId={coachId} clients={clients} lang={lang} />
        )}
        {activeTab === 'notes' && (
          <CoachNotesTab coachId={coachId} clients={clients} lang={lang} />
        )}
        {activeTab === 'toolkit' && (
          <CoachToolkitTab coachId={coachId} clients={clients} lang={lang} tier={tier} />
        )}
        {activeTab === 'readiness' && (
          <CoachReadinessTab coachId={coachId} clients={clients} lang={lang} tier={tier} />
        )}
        {activeTab === 'analytics' && (
          <CoachAnalyticsTab coachId={coachId} clients={clients} lang={lang} tier={tier} />
        )}
        {activeTab === 'teams' && allowedTabs.includes('teams') && (
          <CoachTeamsTab coachId={coachId} tier={tier} clients={clients} lang={lang} />
        )}
        {activeTab === 'schedule' && allowedTabs.includes('schedule') && (
          <CoachScheduleTab coachId={coachId} clients={clients} lang={lang} tier={tier} />
        )}
        {activeTab === 'tournaments' && allowedTabs.includes('tournaments') && (
          <CoachTournamentsTab coachId={coachId} clients={clients} lang={lang} tier={tier} />
        )}
        {activeTab === 'gym' && allowedTabs.includes('gym') && (
          <CoachGymTab coachId={coachId} clients={clients} lang={lang} tier={tier} />
        )}
        {activeTab === 'wallet' && allowedTabs.includes('wallet') && (
          <CoachWalletTab coachId={coachId} clients={clients} lang={lang} />
        )}

        {/* Upgrade prompt for locked tabs */}
        {!allowedTabs.includes(activeTab) && (
          <div style={{ textAlign: 'center', padding: '48px 16px' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
            <div style={{ color: C.white, fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              {nl ? 'Upgrade vereist' : 'Upgrade Required'}
            </div>
            <div style={{ color: C.silver, fontSize: 13, marginBottom: 24 }}>
              {nl ? `Dit onderdeel is beschikbaar vanaf het Club-pakket.` : `This feature is available from the Club package and above.`}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 240, margin: '0 auto' }}>
              {[
                { tier: 'club', price: '€149', color: '#4AC87A' },
                { tier: 'pro', price: '€299', color: '#C8A84B' },
                { tier: 'elite', price: '€499', color: '#7B6FD0' },
              ].filter(t => {
                const tiers = ['solo', 'club', 'pro', 'elite'];
                return tiers.indexOf(t.tier) > tiers.indexOf(tier);
              }).map(opt => (
                <div key={opt.tier} style={{ background: `${opt.color}15`, border: `1px solid ${opt.color}44`, borderRadius: 12, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: opt.color, fontWeight: 700, fontSize: 14 }}>{opt.tier.charAt(0).toUpperCase() + opt.tier.slice(1)}</span>
                  <span style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{opt.price}/mo</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
